import { supabase } from '../lib/supabase';

/**
 * Service for reading/writing admin-configurable app settings
 * stored in the `app_config` Supabase table.
 *
 * Table schema:
 *   config_key  text   PRIMARY KEY
 *   value       jsonb  NOT NULL
 *   description text
 *   updated_at  timestamptz  DEFAULT now()
 *   updated_by  uuid   REFERENCES auth.users(id)
 */

// Default values used as fallback when a key is missing from the DB
export const CONFIG_DEFAULTS = {
  brain_games_daily_limit: 3,
  calendar_view_breakpoint: 1024,
  tool_access_by_group: null,
  landing_intro_messages: [
    { text: "A place built with one purpose —", style: "light" },
    { text: "to make every minute an investment in your life.", style: "bold" },
  ],
  beta_features: [],
};

export const AppConfigService = {
  /** Fetch every row from app_config. Returns { [config_key]: value } map. */
  async getAll() {
    const { data, error } = await supabase
      .from('app_config')
      .select('config_key, value');

    if (error) {
      console.error('AppConfigService.getAll error:', error);
      return { ...CONFIG_DEFAULTS };
    }

    const map = { ...CONFIG_DEFAULTS };
    for (const row of data) {
      map[row.config_key] = row.value;
    }
    return map;
  },

  /** Fetch a single config value. Returns the value or the default. */
  async get(configKey) {
    const { data, error } = await supabase
      .from('app_config')
      .select('value')
      .eq('config_key', configKey)
      .maybeSingle();

    if (error) {
      console.error(`AppConfigService.get(${configKey}) error:`, error);
      return CONFIG_DEFAULTS[configKey] ?? null;
    }

    return data?.value ?? CONFIG_DEFAULTS[configKey] ?? null;
  },

  /** Upsert a single config value (admin-only via RLS). */
  async set(configKey, value, description = null) {
    // Get current user id for updated_by
    const { data: { user } } = await supabase.auth.getUser();

    const payload = {
      config_key: configKey,
      value,
      updated_at: new Date().toISOString(),
      updated_by: user?.id || null,
    };
    if (description !== null) payload.description = description;

    const { data, error } = await supabase
      .from('app_config')
      .upsert(payload, { onConflict: 'config_key' })
      .select()
      .single();

    if (error) {
      console.error(`AppConfigService.set(${configKey}) error:`, error);
      throw error;
    }
    return data;
  },

  /** Batch-upsert multiple config values. configs = [{ key, value, description? }] */
  async setMultiple(configs) {
    const { data: { user } } = await supabase.auth.getUser();
    const now = new Date().toISOString();

    const rows = configs.map(c => ({
      config_key: c.key,
      value: c.value,
      updated_at: now,
      updated_by: user?.id || null,
      ...(c.description !== undefined ? { description: c.description } : {}),
    }));

    const { data, error } = await supabase
      .from('app_config')
      .upsert(rows, { onConflict: 'config_key' })
      .select();

    if (error) {
      console.error('AppConfigService.setMultiple error:', error);
      throw error;
    }
    return data;
  },
};
